import { Component, DestroyRef, effect, inject, Injector, input, OnInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { OrgAdminEndpointsService } from '../../../services/org-service/org-admin-endpoints.service';
import { catchError, EMPTY, finalize, map, Observable, of, switchMap } from 'rxjs';
import { UserDto } from '../org-model/user-dto.interface';
import { AsyncPipe } from '@angular/common';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { OrgUserDetailsTestIds } from './org-user-details.test-ids';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'app-org-user-details',
  imports: [
    MatIcon,
    AsyncPipe,
    NgxSkeletonLoaderComponent,
    FormsModule,
    MatFormField,
    MatInput,
    ReactiveFormsModule,
    MatTooltip,
    MatLabel
  ],
  templateUrl: './org-user-details.component.html',
  styleUrl: './org-user-details.component.scss'
})
/**
 * Zeigt die User Details an.
 *
 * A_25473 - Akteur abmelden
 */
export class OrgUserDetailsComponent implements OnInit {
  userId = input.required<string>();
  domain = input.required<string>();
  isLoading = false;
  user$!: Observable<UserDto>;
  editDisplaynameOpen = false;
  newDisplayName = '';
  protected readonly testIds = OrgUserDetailsTestIds;
  private readonly orgAdminEndpoints = inject(OrgAdminEndpointsService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  ngOnInit(): void {
    effect(
      () => {
        this.loadUserData();
      },
      { injector: this.injector }
    );
  }

  deleteUser(): void {
    this.orgAdminEndpoints
      .deleteUser(this.domain(), this.userId())
      .pipe(
        catchError(() => {
          this.snackbarService.error('Der Nutzer konnte nicht deaktiviert werden');
          throw new Error('User could not be deleted');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.route.navigate(['../../'], { relativeTo: this.activatedRoute });
        this.snackbarService.success('Der Nutzer wurde deaktiviert');
      });
  }

  changeDisplayname(): void {
    const newDisplayname = this.newDisplayName.trim();

    const domain = this.domain();
    const userId = this.userId();

    this.orgAdminEndpoints
      .changeUserDisplayname(domain, userId, newDisplayname)
      .pipe(
        catchError(err => {
          // WORKAROUND TIME-2734
          // Backend liefert aktuell ungültigen CORS Header
          if (err.status === 0) {
            return this.orgAdminEndpoints.getUser(domain, userId);
          }
          this.snackbarService.error('Der Anzeigename konnte nicht erfolgreich geändert werden');
          return EMPTY;
        }),
        switchMap(response => {
          const updatedUser = response.result;

          if (updatedUser.displayname !== newDisplayname) {
            this.snackbarService.error('Der Anzeigename konnte nicht erfolgreich geändert werden');
            return EMPTY;
          }

          this.user$ = of(updatedUser);
          this.cancelDisplaynameEdit();
          this.snackbarService.success('Der Anzeigename des Nutzers wurde erfolgreich geändert');

          const systemMsgToUser = {
            user_id: userId,
            content: {
              body:
                'Ihr Anzeigename (Displayname) wurde aktualisiert. ' +
                'Die Änderung wird nach Ihrem nächsten Login wirksam. ' +
                'Bei Rückfragen wenden Sie sich bitte an Ihre zuständigen Org-Admins.',
              msgtype: 'm.text'
            }
          };

          return this.orgAdminEndpoints.sendSysNotification(domain, systemMsgToUser).pipe(
            catchError(err => {
              console.error('Error while sending displayname system notification', err);
              this.snackbarService.error('Die Systemmeldung zur Anzeigenamenänderung konnte nicht versendet werden');
              return EMPTY;
            }),
            map(() => updatedUser)
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  openDisplaynameEdit(): void {
    this.editDisplaynameOpen = true;
    this.newDisplayName = '';
  }

  cancelDisplaynameEdit(): void {
    this.editDisplaynameOpen = false;
    this.newDisplayName = '';
  }

  private loadUserData(): void {
    this.isLoading = true;
    this.user$ = this.orgAdminEndpoints.getUser(this.domain(), this.userId()).pipe(
      map(userResponse => userResponse.result),
      catchError(() => {
        this.isLoading = false;
        this.snackbarService.error('Fehler beim Laden der Details');
        throw new Error('Could not load Userdetails');
      }),
      finalize(() => {
        this.isLoading = false;
      })
    );
  }
}
